import api from "./axios";
import { safeStorage } from "../utils/storage";

export const removeCustomDomain = async (storeId) => {
  if (!storeId) return null;

  const response = await api.delete(`/stores/${storeId}/remove_custom_domain`);
  return response.data;
};

export const fetchStore = async (storeId) => {
  if (!storeId) return null;

  const response = await api.get(`/stores/${storeId}`);
  return response.data;
};

export const fetchStores = async () => {
  const response = await api.get("/stores");
  return response.data;
};

// Owner first, then admins, then members. Each row carries scans_30d: an
// integer on Growth and above, null on Starter (the API skips the count there).
export const fetchStoreUsers = async (storeId) => {
  if (!storeId) return null;

  const response = await api.get(`/stores/${storeId}/store_users`);
  return response.data.store_users;
};

export const fetchPasses = async (storeId) => {
  if (!storeId) return null;

  const response = await api.get(`/stores/${storeId}/passes`);
  return response.data;
};

export const fetchPassLayout = async (storeId, passId) => {
  if (!storeId) return null;

  const response = await api.get(`/stores/${storeId}/passes/${passId}`);
  return response.data;
};

export const fetchStoreSetup = async (storeId) => {
  if (!storeId) return null;

  const response = await api.get(`/stores/${storeId}/check_setup`);

  safeStorage.set(`setupStatus_${storeId}`, response.data);

  return response.data;
};

export const fetchForms = async (storeId) => {
  if (!storeId) return null;

  const response = await api.get(`/stores/${storeId}/forms`);
  return response.data;
};

export const fetchForm = async (storeId, formId) => {
  if (!storeId) return null;

  const response = await api.get(`/stores/${storeId}/forms/${formId}`);
  return response.data;
};

export const fetchPages = async (storeId) => {
  if (!storeId) return null;

  const response = await api.get(`/stores/${storeId}/pages`);
  return response.data;
};

export const fetchPage = async (storeId, pageId) => {
  if (!storeId) return null;

  const response = await api.get(`/stores/${storeId}/pages/${pageId}`);
  return response.data;
};

export const fetchFlyers = async (storeId) => {
  if (!storeId) return null;

  const response = await api.get(`/stores/${storeId}/flyers`);
  return response.data;
};

export const fetchFlyer = async (storeId, flyerId) => {
  if (!storeId) return null;

  const response = await api.get(`/stores/${storeId}/flyers/${flyerId}`);
  return response.data;
};

export const fetchPublicForm = async (formId) => {
  const response = await api.get(`/public/forms/${formId}`);
  return response.data;
};

export const fetchPublicCustomer = async (customerId) => {
  const response = await api.get(`/public/customers/${customerId}`);
  return response.data;
};

export const fetchPhoneCode = async (storeId) => {
  const params = storeId ? { store_id: storeId } : {};
  const response = await api.get("/public/phone_codes/detect", { params });
  return response.data;
};

// The dashboard's customers list. `view: "list"` asks for the light rows
// (last_visit_at, visits, pass {id, name, type, tier}); the scanner calls the
// same endpoint without it and keeps the default shape. `segment` is one of
// the health segments and is a Growth feature: below that the API answers 403
// plan_feature_locked, an unknown one 422 segment_unknown. The list renders
// those inline, so the request is silent and no toast repeats them.
export const fetchCustomers = async (
  storeId,
  { page = 1, query = null, passId = "all", segment = null } = {},
) => {
  if (!storeId) throw new Error("Store ID is required");

  const params = { page, per_page: 25, view: "list" };
  if (query && query.length >= 3) {
    params.query_string = query;
  }
  if (passId && passId !== "all") {
    params.pass_id = passId;
  }
  if (segment) {
    params.segment = segment;
  }

  const response = await api.get(`/stores/${storeId}/customers`, {
    params,
    silent: true,
  });
  return response.data;
};

export const fetchCustomer = async (storeId, customerId) => {
  if (!storeId) throw new Error("Store ID is required");
  if (!customerId) throw new Error("Customer ID is required");
  const response = await api.get(`/stores/${storeId}/customers/${customerId}`);
  return response.data;
};

// The authenticated show route takes only the numeric id in its path (a uuid
// there is a 404 by design); a uuid goes in ?uuid= against id 0, the same call
// the scanner makes for a scanned QR code.
export const fetchCustomerByUuid = async (storeId, uuid, { silent } = {}) => {
  if (!storeId) throw new Error("Store ID is required");
  if (!uuid) throw new Error("Customer UUID is required");
  const response = await api.get(`/stores/${storeId}/customers/0`, {
    params: { uuid },
    silent,
  });
  return response.data;
};

export const searchIcons = async (query) => {
  const response = await api.get(
    `/icons/search?query=${encodeURIComponent(query)}&page=1&per_page=20`,
  );
  return response.data;
};

export const fetchCampaigns = async (storeId) => {
  if (!storeId) return null;

  const response = await api.get(`/stores/${storeId}/campaigns`);
  return response.data;
};

export const fetchBillingPortalUrl = async (storeId) => {
  if (!storeId) return null;

  const response = await api.get(`/stores/${storeId}/checkout/billing_portal`);
  return response.data;
};

// Dashboard analytics (DASHBOARD_ANALYTICS_PLAN.md). Responses are read raw,
// snake_case. `range` is 7, 30 or 90 days; the API answers 422 for anything else.
export const fetchDashboardOverview = async (storeId, range) => {
  if (!storeId) return null;

  const response = await api.get(`/stores/${storeId}/dashboard/overview`, {
    params: { range },
  });
  return response.data;
};

// One real number per locked tab, for the sneak-peek card. Every tier may call it.
export const fetchDashboardTeaser = async (storeId) => {
  if (!storeId) return null;

  const response = await api.get(`/stores/${storeId}/dashboard/teaser`);
  return response.data;
};

export const fetchDashboardInsights = async (storeId, range) => {
  if (!storeId) return null;

  const response = await api.get(`/stores/${storeId}/dashboard/insights`, {
    params: { range },
  });
  return response.data;
};

export const fetchSegmentCustomers = async (
  storeId,
  segment,
  { page = 1, passId } = {},
) => {
  if (!storeId || !segment) return null;

  const params = { page };
  if (passId) params.pass_id = passId;
  const response = await api.get(
    `/stores/${storeId}/dashboard/segments/${encodeURIComponent(segment)}/customers`,
    { params },
  );
  return response.data;
};

export const fetchDashboardAdvanced = async (storeId) => {
  if (!storeId) return null;

  const response = await api.get(`/stores/${storeId}/dashboard/advanced`);
  return response.data;
};

export const fetchDashboardExport = async (storeId, exportId) => {
  if (!storeId || !exportId) return null;

  const response = await api.get(
    `/stores/${storeId}/dashboard/exports/${exportId}`,
  );
  return response.data;
};

export const fetchBranches = async (storeId) => {
  if (!storeId) return null;

  const response = await api.get(`/stores/${storeId}/branches`);
  return response.data;
};

export const fetchNotifications = async (storeId) => {
  if (!storeId) return null;

  const response = await api.get(`/stores/${storeId}/notifications`);
  return response.data;
};

export const fetchUser = async () => {
  const response = await api.get("/user/me");
  return response.data;
};

export const fetchUserIdentity = async () => {
  const response = await api.get("/user/identity");
  return response.data;
};

export const fetchIntegrations = async (storeId) => {
  if (!storeId) return null;

  const response = await api.get(`/stores/${storeId}/integrations`);
  return response.data;
};

export const fetchIntegration = async (storeId, integrationId) => {
  if (!storeId) return null;

  const response = await api.get(
    `/stores/${storeId}/integrations/${integrationId}`,
  );
  return response.data;
};

export const fetchtTwilioPhoneNumbers = async (storeId, integrationId) => {
  if (!storeId) return null;

  const response = await api.get(
    `/stores/${storeId}/integrations/${integrationId}/phoneNumbers`,
    {
      params: {
        integration: { id: integrationId },
      },
    },
  );
  return response.data;
};

export const fetchIntegrationsEmailAddresses = async (
  storeId,
  integrationId,
) => {
  if (!storeId) return null;

  const response = await api.get(
    `/stores/${storeId}/integrations/${integrationId}/emailAddresses`,
    {
      params: {
        integration: { id: integrationId },
      },
    },
  );
  return response.data;
};

// silent: the integrations drawer shows these failures inline; the deliveries
// list polls every 10 s, so a global toast would repeat on every poll.
export const fetchIntegrationAudiences = async (
  storeId,
  integrationId,
  { silent } = {},
) => {
  if (!storeId) return null;

  const response = await api.get(
    `/stores/${storeId}/integrations/${integrationId}/audiences`,
    {
      params: {
        integration: { id: integrationId },
      },
      silent,
    },
  );
  return response.data;
};

export const fetchWebhookEvents = async (
  storeId,
  integrationId,
  { silent } = {},
) => {
  if (!storeId) return null;

  const response = await api.get(
    `/stores/${storeId}/integrations/${integrationId}/webhook_events`,
    { silent },
  );
  return response.data;
};

export const fetchMessages = async (storeId) => {
  if (!storeId) return null;

  const response = await api.get(`/stores/${storeId}/messages`);
  return response.data;
};

export const exportCustomers = async (storeId, { startAt, endAt } = {}) => {
  if (!storeId) return null;

  try {
    const params = {};
    if (startAt) params.start_at = startAt;
    if (endAt) params.end_at = endAt;

    // Use axios for the request since it handles auth automatically
    const response = await api.get(`/stores/${storeId}/customers/export`, {
      responseType: "blob", // Important: tell axios to expect binary data
      params,
    });

    // Get filename from custom X-Filename header
    const customFilename =
      response.headers["x-filename"] || response.headers["X-Filename"];

    let filename = "customers_export.xlsx"; // fallback

    if (customFilename) {
      filename = customFilename;
    }

    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename; // Use the server-provided filename
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  } catch (error) {
    console.error("Export failed:", error);
    throw error;
  }
};

export const exportTransactions = async (storeId, { startAt, endAt } = {}) => {
  if (!storeId) return null;

  try {
    const params = {};
    if (startAt) params.start_at = startAt;
    if (endAt) params.end_at = endAt;

    // Use axios for the request since it handles auth automatically
    const response = await api.get(
      `/stores/${storeId}/customers/export_transactions`,
      {
        responseType: "blob", // Important: tell axios to expect binary data
        params,
      },
    );

    // Get filename from custom X-Filename header
    const customFilename =
      response.headers["x-filename"] ||
      response.headers["X-Filename"] ||
      response.headers.get?.("x-filename") ||
      response.headers.get?.("X-Filename");

    let filename = "transactions_export.xlsx"; // fallback

    if (customFilename) {
      filename = customFilename;
    }

    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename; // Use the server-provided filename
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  } catch (error) {
    console.error("Export failed:", error);
    throw error;
  }
};

export const fetchMessage = async (storeId, messageId) => {
  if (!storeId) return null;

  const response = await api.get(`/stores/${storeId}/messages/${messageId}`);
  return response.data;
};

// The card's most recent expiration run: progress while it works, and
// afterwards whether there is still something to undo. 204 means the card has
// never had one.
export const fetchPassExpirationRun = async (storeId, passId) => {
  if (!storeId || !passId) return null;

  const response = await api.get(
    `/stores/${storeId}/passes/${passId}/expiration_run`,
  );
  return response.status === 204 ? null : response.data;
};
